import React, { useRef } from 'react';
import { FaPenFancy, FaRocket, FaCheckCircle } from 'react-icons/fa';
import RoughBorder from '../../kits/LayoutKit/RoughBorder';
import MonoButton from '../../kits/DesignKit/MonoButton';

/**
 * Wiederverwendbare ZenPost Pricing-Section Komponente
 * Zeigt Pricing Cards mit RoughBorder und mobilem Design
 */
const ZenPostPricingSection = ({
  title = "Einfacher Preis. Keine Überraschungen.",
  subtitle = "ZenStudio dauerhaft kostenlos. Pro für Teams und Cloud Doc Studio.",
  plans = null,
  showEnterpriseContact = true,
  enterpriseText = "Enterprise oder Custom Plan?",
  enterpriseEmail = "saghallo@denisbitter.de",
  onCTAClick = null,
  theme = "light"
}) => {
  const pricingCard1Ref = useRef();
  const pricingCard2Ref = useRef();

  // Default Pricing Plans
  const defaultPlans = [
    {
      name: 'ZenEditor',
      price: 'Kostenlos',
      emoji: '✍️',
      icon: <FaPenFancy className="text-brand" />,
      description: 'Reines Schreiben im Web oder als Desktop-App',
      features: [
        'Reines Schreiben (Web oder Desktop)',
        'Lokale KI mit Ollama (optional)',
        'Lokale Datenspeicherung',
        'Kein Publisher',
        'Kein Doc Studio',
        '100% Offline',
        'Unbegrenzte Nutzung'
      ],
      cta: 'Live Demo starten',
      popular: false,
      bgColor: 'transparent',
      ref: pricingCard1Ref
    },
    {
      name: 'ZenPost Studio Pro',
      price: '79€',
      period: '/Lifetime',
      emoji: '🚀',
      icon: <FaRocket className="text-brand" />,
      description: 'Web + Desktop + Cloud-Sync + Doc Studio',
      features: [
        'Alles aus Desktop',
        'Cloud-Sync über alle Geräte',
        'Team-Kollaboration (bis 5 User)',
        'Analytics & Insights',
        'Custom Templates',
        'Priority Support',
        'Versionierung & History',
        'API-Zugriff (Basic)',
        'Doc Studio',
        'KI Anbindung an andere KI möglich'
      ],
      cta: 'Pro Anfragen',
      popular: true,
      bgColor: 'transparent',
      ref: pricingCard2Ref
    }
  ];

  const pricingPlans = plans || defaultPlans;

  const isDark = theme === "dark";

  return (
    <section className={isDark ? "py-14 sm:py-20 px-4 text-brand" : "py-12 sm:py-16 px-4 "}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12 px-4">
          <div className={isDark ? "inline-flex items-center rounded-full border border-brand px-3 py-1 text-xs  text-[#EFEBDC]" : "hidden"}>
            ZenStudio Preiscard
          </div>
          <h2 className={isDark ? "mt-4 text-3xl sm:text-4xl md:text-5xl font-extrabold" : "text-2xl sm:text-3xl md:text-5xl font-black text-gray-900 mb-3 sm:mb-4"}>
            {title}
          </h2>
          <p className={isDark ? "mt-3 text-base sm:text-lg text-[#EFEBDC] max-w-3xl mx-auto" : "text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto"}>
            {subtitle}
          </p>
        </div>

        {/* Pricing Cards Grid */}
        <div className="grid md:grid-cols-2 gap-6 sm:gap-8 mb-8 sm:mb-12 px-4">
          {pricingPlans.map((plan, index) => {
            const cardRef = index === 0 ? pricingCard1Ref : pricingCard2Ref;
            return (
              <div key={index} ref={cardRef} className="relative h-full">
                <div className={isDark ? "relative overflow-hidden rounded-2xl border border-brand  p-6 sm:p-8   shadow-slate-800 shadow-lg h-full flex flex-col" : "bg-gradient-to-br from-white via-brand/5 to-white p-6 sm:p-8 rounded-lg shadow-lg transition-all hover:shadow-xl h-full flex flex-col"}>
                  {/* Popular Badge */}
                  {plan.popular && (
                    <div className={isDark ? "absolute right-6 top-6 rounded-full px-3 py-1 text-xs font-semibold  border border-brand" : "mb-3 sm:mb-0"}>Most Popular</div>
                  )}

                  {/* Icon Header - NUR FA Icon, kein Emoji */}
                  <div className="mb-3 sm:mb-4">
                    <div className={isDark ? "text-2xl sm:text-3xl text-[#EFEBDC] " : "text-2xl sm:text-2xl"}>{plan.icon}</div>
                  </div>

                  {/* Plan Name */}
                  <h3 className={isDark ? "text-xl sm:text-2xl md:text-3xl font-extrabold text-[#EFEBDC] mb-2 sm:mb-3" : "text-xl sm:text-2xl md:text-3xl font-black text-gray-900 mb-2 sm:mb-3"}>
                    {plan.name}
                  </h3>

                  {/* Description - Fixed Height */}
                  <p className={isDark ? "text-sm sm:text-base text-brand mb-4 sm:mb-6 min-h-[3rem]" : "text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 min-h-[3rem]"}>
                    {plan.description}
                  </p>

                  {/* Price */}
                  <div className="mb-4 sm:mb-6">
                    <span className={isDark ? "text-4xl sm:text-5xl md:text-6xl font-extrabold text-[#EFEBDC]" : "text-4xl sm:text-5xl md:text-6xl font-black text-gray-900"}>
                      {plan.price}
                    </span>
                    {plan.period && (
                      <span className={isDark ? "text-base sm:text-lg text-amber-50 ml-1" : "text-base sm:text-lg text-gray-600 ml-1"}>
                        {plan.period}
                      </span>
                    )}
                  </div>

                  {/* Features List - Fixed Min Height */}
                  <ul className="space-y-2 sm:space-y-3 mb-4 sm:mb-6 min-h-[18rem] md:min-h-[20rem]">
                    {plan.features.map((feature, i) => (
                      <li key={i} className={isDark ? "flex items-start gap-2 text-xs sm:text-sm text-amber-50" : "flex items-start gap-2 text-xs sm:text-sm text-gray-700"}>
                        <FaCheckCircle className={isDark ? "text-brand flex-shrink-0 mt-0.5" : "text-brand flex-shrink-0 mt-0.5"} />
                        <span className="flex-shrink-0">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button - At Bottom */}
                  <div className="mt-auto">
                    <MonoButton
                      onClick={() => {
                        if (onCTAClick) onCTAClick(plan);
                        if (plan.ctaAction) plan.ctaAction();
                      }}
                      className="rounded-full border-brand bg-transparent px-6 py-3 text-sm sm:text-base font-semibold shadow-lg shadow-slate-900/10 transition w-full"
                      textColor="text-brand"
                      underlineColors="ZenBrand"
                    >
                      {plan.cta}
                    </MonoButton>
                  </div>
                </div>

                {/* RoughBorder */}
                {!isDark && (
                  <RoughBorder
                    parentRef={cardRef}
                    radius={10}
                    padding={2}
                    color={plan.popular ? "#AC8E66" : "#AC8E66"}
                    strokeWidth={plan.popular ? 0.4 : 0.3}
                    roughness={1}
                    bowing={2}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Enterprise Contact */}
        {showEnterpriseContact && (
          <div className="text-center px-4">
            <p className={isDark ? "text-sm sm:text-base text-[#EFEBDC]" : "text-sm sm:text-base text-gray-600"}>
              {enterpriseText}
            </p>
            <div className="mt-3 inline-block">
              <MonoButton
                onClick={() => {
                  window.location.href = `mailto:${enterpriseEmail}?subject=ZenPost Studio Enterprise`;
                }}
                className="rounded-full border-brand bg-transparent px-6 py-2 text-sm sm:text-sm shadow-lg shadow-slate-900/10 transition"
                textColor="text-brand"
                underlineColors="ZenBrand"
              >
                Lass uns sprechen
              </MonoButton>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ZenPostPricingSection;
